import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import type { AsideActionPosition } from '~/types/global'
import { AsideActionContext } from '~/utils/contextes'

export function AsideActionProvider({ children }: { children: ReactNode }) {
  const [asideAction, setAsideAction] = useState<ReactNode | null>(null)
  const [isWideAside, setIsWideAside] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [position, setPosition] = useState<AsideActionPosition>('sidebar')
  const [availablePositions, setAvailablePositions] = useState<
    AsideActionPosition[]
  >(['sidebar'])

  const setAction = useCallback(
    ({
      action,
      isWide,
      position,
      availablePositions
    }: {
      action: ReactNode
      isWide?: boolean
      position?: AsideActionPosition
      availablePositions?: AsideActionPosition[]
    }) => {
      setAsideAction(action)
      setIsDirty(false)
      setIsWideAside(isWide ?? false)
      setPosition(position ?? 'sidebar')
      setAvailablePositions(availablePositions ?? ['sidebar'])
    },
    []
  )

  const resetAction = useCallback(() => {
    setAsideAction(null)
    setIsDirty(false)
  }, [])

  useEffect(() => {
    // Sometimes asideAction component set isDirty to true while being unmounted via resetAction()
    // This is a workaround to prevent this behavior
    if (asideAction === null && isDirty) {
      setIsDirty(false)
    }
  }, [asideAction, isDirty])

  return (
    <AsideActionContext.Provider
      value={{
        asideAction,
        isDirty,
        isWide: isWideAside,
        setAsideAction: setAction,
        setPosition,
        setIsDirty,
        position,
        availablePositions,
        resetAction
      }}
    >
      {children}
    </AsideActionContext.Provider>
  )
}
